import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiUrl } from "../utils/backendUrl";
import { getTenantPreviewSlug } from "../utils/tenantPreview";

const axiosOpts = { withCredentials: true };

function buildParamsQuery(path, params = {}) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, value);
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useAdminDashboard() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "dashboard", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl("/api/v1/admin/dashboard"),
        axiosOpts
      );
      return data;
    },
    retry: false,
    staleTime: 30_000,
  });
}

// ─── Users (paginated, searchable, filterable) ─────────────────────────────

export function useAdminUsers(
  query = "",
  filters = {},
  page = 1,
  limit = 25,
  sortBy = "createdAt",
  sortOrder = "desc"
) {
  return useQuery({
    queryKey: ["admin", "users", query, filters, page, limit, sortBy, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (filters.role) params.set("role", filters.role);
      if (filters.badge) params.set("badge", filters.badge);
      if (filters.category) params.set("category", filters.category);
      if (filters.status) params.set("status", filters.status);
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);

      const { data } = await axios.get(
        apiUrl(`/api/v1/admin/users?${params.toString()}`),
        axiosOpts
      );
      return data;
    },
    retry: false,
    keepPreviousData: true,
  });
}

// ─── Single User ────────────────────────────────────────────────────────────

export function useAdminUser(id) {
  return useQuery({
    queryKey: ["admin", "user", id],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl(`/api/v1/admin/users/${id}`),
        axiosOpts
      );
      return data;
    },
    enabled: !!id,
    retry: false,
  });
}

// ─── Create User ────────────────────────────────────────────────────────────

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post(
        apiUrl("/api/v1/admin/users"),
        payload,
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useImportUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post(
        apiUrl("/api/v1/admin/users/import"),
        payload,
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Update User ────────────────────────────────────────────────────────────

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data } = await axios.patch(
        apiUrl(`/api/v1/admin/users/${id}`),
        updates,
        axiosOpts
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", variables.id] });
    },
  });
}

// ─── Update Clan Role ───────────────────────────────────────────────────────
// Changes the member's role INSIDE THE CURRENT CLAN (TenantMembership.role),
// not their platform account role. The clan comes from the server's tenant
// context, so nothing clan-identifying is sent. The server refuses to touch
// your own role or the owner's, and only an owner may grant/revoke
// tenant_admin (403 with the reason).
// Endpoint: PATCH /api/v1/admin/users/:id/clan-role  body { clanRole }.

export function useUpdateClanRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, clanRole }) => {
      const { data } = await axios.patch(
        apiUrl(`/api/v1/admin/users/${id}/clan-role`),
        { clanRole },
        axiosOpts
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", variables.id] });
      qc.invalidateQueries({ queryKey: ["tenant", "roles"] });
    },
  });
}

// ─── Delete User ────────────────────────────────────────────────────────────

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.delete(
        apiUrl(`/api/v1/admin/users/${id}`),
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Remove From Clan (non-destructive) ─────────────────────────────────────
// Removes only the member's TenantMembership for the current clan — the user
// account itself is preserved (unlike useDeleteUser). Server blocks removing
// the founder or yourself (400) and requires clan-manage permission (403).
// Endpoint: DELETE /api/v1/workspace/roles/members/:membershipId.

export function useRemoveClanMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membershipId) => {
      const { data } = await axios.delete(
        apiUrl(`/api/v1/workspace/roles/members/${membershipId}`),
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["tenant", "roles"] });
    },
  });
}

// ─── Ban / Unban From Clan (membership-scoped) ──────────────────────────────
// Suspends or restores only the member's TenantMembership for the current clan.
// It never toggles the global User.banned account flag.
// Endpoint: POST /api/v1/workspace/roles/members/:membershipId/ban.

export function useBanClanMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ membershipId, banned = true }) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/workspace/roles/members/${membershipId}/ban`),
        { banned },
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["tenant", "roles"] });
    },
  });
}

// ─── Ban / Unban User ───────────────────────────────────────────────────────

export function useBanUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, banned }) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/admin/users/${id}/ban`),
        { banned },
        axiosOpts
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
      qc.invalidateQueries({ queryKey: ["admin", "user", variables.id] });
    },
  });
}

// ─── Validate User (manual email verification) ─────────────────────────────

export function useValidateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/admin/users/${id}/validate`),
        {},
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Reset User Password ───────────────────────────────────────────────────

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/admin/users/${id}/reset-password`),
        {},
        axiosOpts
      );
      return data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["admin", "user", id] });
    },
  });
}

// ─── Subclans ─────────────────────────────────────────────────────────────────

// Admin Audit Trail

export function useAdminAuditTrail(params = {}) {
  return useQuery({
    queryKey: ["admin", "audit-trail", params],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl(buildParamsQuery("/api/v1/admin/audit-trail", params)),
        axiosOpts
      );
      return data;
    },
    retry: false,
    keepPreviousData: true,
    staleTime: 15_000,
  });
}

export function useAdminAuditTrailDetail(id, options = {}) {
  return useQuery({
    queryKey: ["admin", "audit-trail", "detail", id],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl(`/api/v1/admin/audit-trail/${id}`),
        axiosOpts
      );
      return data;
    },
    enabled: Boolean(id) && options.enabled !== false,
    retry: false,
  });
}

export function useAdminSubclans(query = "", page = 1, limit = 25) {
  const offset = (page - 1) * limit;
  return useQuery({
    queryKey: ["admin", "subclans", query, page, limit],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl(
          `/api/admin/subclans?q=${encodeURIComponent(query)}&offset=${offset}&limit=${limit}`
        ),
        axiosOpts
      );
      return data;
    },
    retry: false,
    keepPreviousData: true,
  });
}

export function useAdminSubclan(guid) {
  return useQuery({
    queryKey: ["admin", "subclan", guid],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl(`/api/admin/subclans/${guid}`),
        axiosOpts
      );
      return data;
    },
    enabled: !!guid,
    retry: false,
  });
}

export function useAdminSubclanMembers(
  guid,
  { query = "", page = 1, limit = 25, status = "" } = {}
) {
  const offset = (page - 1) * limit;
  return useQuery({
    queryKey: ["admin", "subclanMembers", guid, query, page, limit, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (status) params.set("status", status);
      params.set("offset", String(offset));
      params.set("limit", String(limit));

      const { data } = await axios.get(
        apiUrl(`/api/admin/subclans/${guid}/members?${params.toString()}`),
        axiosOpts
      );
      return data;
    },
    enabled: !!guid,
    retry: false,
    keepPreviousData: true,
  });
}

// ─── Update Subclan ────────────────────────────────────────────────────────────

export function useUpdateSubclan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ guid, updates }) => {
      const { data } = await axios.patch(
        apiUrl(`/api/admin/subclans/${guid}`),
        updates,
        axiosOpts
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin", "subclans"] });
      qc.invalidateQueries({ queryKey: ["admin", "subclan", variables.guid] });
      qc.invalidateQueries({
        queryKey: ["admin", "subclanMembers", variables.guid],
      });
    },
  });
}

// ─── Delete Subclan ────────────────────────────────────────────────────────────

export function useDeleteSubclan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.delete(
        apiUrl(`/api/v1/subclans/${id}`),
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "subclans"] });
    },
  });
}

// ─── Events ─────────────────────────────────────────────────────────────────

// GET /api/v1/events is Clan-scoped on the server (T-033): it answers with the
// Clan resolved from the request, so `apiUrl` has to carry the active Clan
// (`?tenant=<slug>` on the shared host, the custom domain's own host
// otherwise) and the cache key has to name it too — otherwise switching Clan
// would serve the previous Clan's events out of the React Query cache.
export function useAdminEvents(query = "", page = 1, limit = 25) {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "events", tenantPreviewSlug, query, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      params.set("page", String(page));
      params.set("limit", String(limit));
      const { data } = await axios.get(
        apiUrl(`/api/v1/events?${params.toString()}`),
        axiosOpts
      );
      return data;
    },
    retry: false,
    keepPreviousData: true,
  });
}

// ─── Update Event ────────────────────────────────────────────────────────────

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data } = await axios.patch(
        apiUrl(`/api/v1/events/${id}`),
        updates,
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
    },
  });
}

// ─── Delete Event ────────────────────────────────────────────────────────────

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.delete(
        apiUrl(`/api/v1/events/${id}`),
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "events"] });
    },
  });
}

// ─── Settings ───────────────────────────────────────────────────────────────

export function useAdminSettings() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "settings", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl("/api/admin/config"),
        axiosOpts
      );
      return data;
    },
    retry: false,
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.put(
        apiUrl("/api/admin/config"),
        payload,
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
    },
  });
}

// ─── Clan App Integrations ─────────────────────────────────────────────────

export function useClanApps(options = {}) {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "clan-apps", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(apiUrl("/api/v1/clan-apps"), axiosOpts);
      return data?.data?.apps || [];
    },
    enabled: options.enabled !== false,
    retry: false,
    staleTime: 15_000,
  });
}

export function useCreateClanApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post(apiUrl("/api/v1/clan-apps"), payload, axiosOpts);
      return data?.data?.app || data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clan-apps"] });
    },
  });
}

export function useUpdateClanApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data } = await axios.patch(apiUrl(`/api/v1/clan-apps/${id}`), updates, axiosOpts);
      return data?.data?.app || data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clan-apps"] });
    },
  });
}

export function useRotateClanAppCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, environmentName = "production" }) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/clan-apps/${id}/credentials/rotate`),
        { environmentName },
        axiosOpts
      );
      return data?.data || data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clan-apps"] });
    },
  });
}

export function useClanAppDevices(appId, options = {}) {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "clan-apps", tenantPreviewSlug, appId, "devices"],
    queryFn: async () => {
      const { data } = await axios.get(apiUrl(`/api/v1/clan-apps/${appId}/devices`), axiosOpts);
      return data?.data?.devices || [];
    },
    enabled: Boolean(appId) && options.enabled !== false,
    retry: false,
    staleTime: 15_000,
  });
}

export function useRevokeClanAppDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ appId, deviceId }) => {
      const { data } = await axios.delete(
        apiUrl(`/api/v1/clan-apps/${appId}/devices/${encodeURIComponent(deviceId)}`),
        axiosOpts
      );
      return data?.data?.device || data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["admin", "clan-apps"] });
    },
  });
}

export function useTestClanAppPush() {
  return useMutation({
    mutationFn: async ({ id, provider = "firebase", platform = "android" }) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/clan-apps/${id}/push/test`),
        { provider, platform },
        axiosOpts
      );
      return data?.data || data;
    },
  });
}

export function useClanAppWebhookDeliveries(appId, options = {}) {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "clan-apps", tenantPreviewSlug, appId, "webhooks", "deliveries"],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl(`/api/v1/clan-apps/${appId}/webhooks/deliveries`),
        axiosOpts
      );
      return data?.data?.events || [];
    },
    enabled: Boolean(appId) && options.enabled !== false,
    retry: false,
    staleTime: 15_000,
  });
}

export function useRotateClanAppWebhookSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ appId, webhookId }) => {
      const { data } = await axios.post(
        apiUrl(`/api/v1/clan-apps/${appId}/webhooks/${webhookId}/secret/rotate`),
        {},
        axiosOpts
      );
      return data?.data || data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clan-apps"] });
    },
  });
}

export function useAdminDomains() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "domains", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(apiUrl("/api/admin/domains"), axiosOpts);
      return data?.data || data || {};
    },
    retry: false,
  });
}

export function useCreateAdminDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await axios.post(apiUrl("/api/admin/domains"), payload, axiosOpts);
      return data?.data || data || {};
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "domains"] });
      qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });
}

export function useVerifyAdminDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.post(apiUrl(`/api/admin/domains/${id}/verify`), {}, axiosOpts);
      return data?.data || data || {};
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "domains"] });
      qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });
}

export function useSetAdminPrimaryDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.patch(
        apiUrl(`/api/admin/domains/${id}/primary`),
        {},
        axiosOpts
      );
      return data?.data || data || {};
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "domains"] });
      qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });
}

export function useDeleteAdminDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await axios.delete(apiUrl(`/api/admin/domains/${id}`), axiosOpts);
      return data?.data || data || {};
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "domains"] });
      qc.invalidateQueries({ queryKey: ["public-config"] });
    },
  });
}

// ─── Tenant Dynamics (from Roles center) ─────────────────────────────────
// Surfaced to admin surfaces (e.g. Subclans allowlist) so admins can pick
// from the clan-wide dynamics list when restricting subclans.
export function useTenantDynamics() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["tenant", "dynamics", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl("/api/v1/workspace/roles"),
        axiosOpts
      );
      const payload = data?.data || data || {};
      return Array.isArray(payload?.dynamics) ? payload.dynamics : [];
    },
    retry: false,
  });
}

// ─── Tenant Roles (from Roles center) ─────────────────────────────────────
// Exposes the role definitions (key + name + signupEnabled) so admin
// surfaces can populate dropdowns — e.g. the login page's "Sign up role"
// selector in Custom Pages.
export function useTenantRoles() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["tenant", "roles", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl("/api/v1/workspace/roles"),
        axiosOpts
      );
      const payload = data?.data || data || {};
      return Array.isArray(payload?.roles) ? payload.roles : [];
    },
    retry: false,
  });
}

// ─── Branding Upload ────────────────────────────────────────────────────────

export function useUploadBranding() {
  return useMutation({
    mutationFn: async ({ file, type }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type); // logo | favicon | touchIcon
      const { data } = await axios.post(
        apiUrl("/api/admin/config/upload-branding"),
        formData,
        {
          ...axiosOpts,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return data;
    },
  });
}

// NOTE: useAdminProfileFields / useCreateProfileField /
// useUpdateProfileField / useDeleteProfileField were removed as part of
// collapsing the parallel ProfileFieldSchema registry into
// PlatformSettings.userFields. Edit user fields via platform admin →
// Settings → User Fields.

// ─── Pages ──────────────────────────────────────────────────────────────────

export function useAdminPages() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "pages", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl("/api/admin/pages"),
        axiosOpts
      );
      return data;
    },
    retry: false,
  });
}

export function useUpdatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug, title, content, config }) => {
      // Only include fields the caller actually passed — preserves the
      // current PATCH-like semantics (omitted keys are left alone) even
      // though the endpoint is PUT.
      const body = {};
      if (title !== undefined) body.title = title;
      if (content !== undefined) body.content = content;
      if (config !== undefined) body.config = config;
      const { data } = await axios.put(
        apiUrl(`/api/admin/pages/${encodeURIComponent(slug)}`),
        body,
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pages"] });
    },
  });
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ title, slug, content }) => {
      const { data } = await axios.post(
        apiUrl("/api/admin/pages"),
        { title, slug, content },
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pages"] });
    },
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug) => {
      const { data } = await axios.delete(
        apiUrl(`/api/admin/pages/${encodeURIComponent(slug)}`),
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pages"] });
    },
  });
}

// ─── Menus ──────────────────────────────────────────────────────────────────

export function useAdminMenus() {
  const tenantPreviewSlug = getTenantPreviewSlug();
  return useQuery({
    queryKey: ["admin", "menus", tenantPreviewSlug],
    queryFn: async () => {
      const { data } = await axios.get(
        apiUrl("/api/admin/menus"),
        axiosOpts
      );
      return data;
    },
    retry: false,
  });
}

export function useUpdateMenu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ slug, name, position, items }) => {
      const payload = {};
      if (name !== undefined) payload.name = name;
      if (position !== undefined) payload.position = position;
      if (items !== undefined) payload.items = items;
      const { data } = await axios.put(
        apiUrl(`/api/admin/menus/${encodeURIComponent(slug)}`),
        payload,
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "menus"] });
    },
  });
}

// ─── Moderation Queue ──────────────────────────────────────────────────────

export function useModerationQueue({
  page = 1,
  type = "all",
  flagType = "all",
  status = "flagged",
  // Lets a caller mounted on every admin route (AdminLayout's nav badge,
  // see AdminLayout.jsx) skip the request entirely for users who could never
  // be authorized for it (the endpoint requires requireTenantAdminOrPlatformAdmin
  // server-side) instead of firing it unconditionally and eating an avoidable
  // 403. Defaults to true so every existing call site (Dashboard.jsx,
  // ModerationQueue.jsx) is unaffected.
  enabled = true,
} = {}) {
  return useQuery({
    queryKey: ["admin", "moderation", page, type, flagType, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("type", type);
      params.set("flagType", flagType);
      params.set("status", status);

      const { data } = await axios.get(
        apiUrl(`/api/v1/admin/moderation/queue?${params.toString()}`),
        axiosOpts
      );
      return data;
    },
    retry: false,
    keepPreviousData: true,
    enabled,
  });
}

export function useApproveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, id }) => {
      const { data } = await axios.patch(
        apiUrl(`/api/v1/admin/moderation/${type}/${id}/approve`),
        {},
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "moderation"] });
    },
  });
}

export function useRejectItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, id }) => {
      const { data } = await axios.patch(
        apiUrl(`/api/v1/admin/moderation/${type}/${id}/reject`),
        {},
        axiosOpts
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "moderation"] });
    },
  });
}

// ─── Flush Cache ────────────────────────────────────────────────────────────

export function useFlushCache() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await axios.post(
        apiUrl("/api/admin/flush-cache"),
        {},
        axiosOpts
      );
      return data;
    },
  });
}
